import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import i18nConfig from 'utils/i18nConfig'
import { DEFAULT_NAMESPACES, SIMULATION_NAMESPACES } from 'constants/i18n'
import { ReferralProps } from 'features/referral/types'
import { faqCategoriesSlugs } from 'constants/faq-categories'
import { FAQCategories } from 'features/faq-page/types'
import { REFERRAL_AMOUNT_GIVE, REFERRAL_AMOUNT_RECEIVE } from 'features/referral/Referral.constants'
import React from 'react'
import Layout from 'components/layout/Layout'
import { useTranslation } from 'next-i18next/pages'
import { useGetLanguage } from 'hooks/useGetLanguage'
import Referral from 'features/referral/Referral'
import Referrer from 'features/referral/Referrer'
import { cmsLib, websiteLib } from 'lib'

const ReferralPage = ({ activeAction, faqCategories, referralCode, videoTestimonials }: ReferralProps) => {
  // i18n
  const { t } = useTranslation('referral')
  const language = useGetLanguage()

  // Constants
  const amountGive = activeAction?.reduction || REFERRAL_AMOUNT_GIVE
  const amountReceive = activeAction?.reduction || REFERRAL_AMOUNT_RECEIVE
  const firstName = referralCode?.referrer.firstName ?? ''

  return (
    <Layout
      seo={{
        description: referralCode
          ? activeAction?.shareLinkSeo?.metaDescription || t('referral.seo.paragraph', { firstName, amountGive, amountReceive })
          : activeAction?.seo?.metaDescription || t('referrer.seo.paragraph', { firstName, amountGive, amountReceive }),
        image:
          (referralCode ? activeAction?.shareLinkSeo?.metaImage?.url : activeAction?.seo?.metaImage?.url) ||
          `/images/seo/referral/seo-refer-${language}.jpg`,
        title: referralCode
          ? activeAction?.shareLinkSeo?.metaTitle || t('referral.seo.title', { firstName, amountGive, amountReceive })
          : activeAction?.seo?.metaTitle || t('referrer.seo.title', { firstName, amountGive, amountReceive })
      }}
    >
      {referralCode ? (
        <Referral {...{ activeAction, faqCategories, referralCode, videoTestimonials }} />
      ) : (
        <Referrer {...{ activeAction, faqCategories, videoTestimonials }} />
      )}
    </Layout>
  )
}

export const getServerSideProps = async ({ locale, query }) => {
  // Get the FAQ questions & referral code
  const [faqCategories, referralCodeResponse, videoTestimonials] = await Promise.all([
    cmsLib.getFaqCategories(faqCategoriesSlugs[FAQCategories.REFERRAL], locale),
    query.referralcode ? websiteLib.checkPromoReferralCode(query.referralcode as string) : undefined,
    cmsLib.getVideoTestimonials(locale)
  ])

  const referralCode = referralCodeResponse?.valid && referralCodeResponse?.referralCode ? referralCodeResponse.referralCode : null

  const activeAction = await cmsLib.getActiveReferralAction(locale, referralCode?.referrer?.firstName || '')

  return {
    props: {
      activeAction: activeAction || null,
      faqCategories,
      referralCode,
      videoTestimonials,
      ...(await serverSideTranslations(locale, [...DEFAULT_NAMESPACES, 'referral', ...SIMULATION_NAMESPACES], i18nConfig))
    }
  }
}

export default ReferralPage
